/**
 * Smart Banner Script
 * Lightweight JavaScript library for dynamic smart banner rendering
 * @version 1.0.0
 * @author Enuygun Tech Team | Eren Emmez
 */

// Import modules
import { SmartBannerError } from "./error/SmartBannerError.js";
import { validateProps } from "./utils/validation.js";
import { fetchBanners } from "./api/api.js";
import { DefaultRenderer } from "./render/DefaultRenderer.js";
import * as sizeUtils from "./utils/sizeUtils.js";

/**
 * Global SmartBanner object
 * Core architecture with cache, batch processing and rendering
 */
window.SmartBanner = {
  // Configuration
  config: {
    apiEndpoint: null, // Will be determined dynamically
    debug: false,
  },

  /**
   * Get dynamic API endpoint based on current domain
   * @returns {string} API endpoint URL
   */
  getApiEndpoint() {
    // If already set via config, use it
    if (this.config.apiEndpoint) {
      return this.config.apiEndpoint;
    }

    // Check if we're in Smart Banner Studio preview mode
    if (
      typeof window !== "undefined" &&
      window.SMART_BANNER_STUDIO_PREVIEW_MODE === true
    ) {
      // Override API URL for Studio preview - use production BFF
      return "https://www.enuygun.com/napi/v1/smart-banner";
    }

    // Determine endpoint based on current hostname
    const hostname = window.location.hostname;

    // Development/localhost
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:8081/napi/v1/smart-banner";
    }

    // Enuygun domains
    if (
      hostname.includes("enuygun.com") ||
      hostname.includes("enuygun.dev") ||
      hostname.includes("enuygun.tech")
    ) {
      return `https://${hostname}/napi/v1/smart-banner`;
    }

    // Wingie domains
    if (
      hostname.includes("wingie.com") ||
      hostname.includes("wingie.co.uk") ||
      hostname.includes("wingie.de") ||
      hostname.includes("wingie.es") ||
      hostname.includes("wingie.ae") ||
      hostname.includes("wingie.qa") ||
      hostname.includes("wingie.pk") ||
      hostname.includes("wingie.co.in")
    ) {
      return `https://${hostname}/napi/v1/smart-banner`;
    }

    // Default fallback (localhost for development)
    return "http://localhost:8081/napi/v1/smart-banner";
  },

  /**
   * Parse banner size from API response
   * @param {string} sizeString - Size string like "boyut768x400"
   * @returns {Object} Size object with width and height
   */
  parseBannerSize(sizeString) {
    if (!sizeString || typeof sizeString !== "string") {
      return { width: "auto", height: "auto" };
    }

    // Extract numbers from string like "boyut768x400"
    const match = sizeString.match(/(\d+)x(\d+)/);
    if (match) {
      return {
        width: `${match[1]}px`,
        height: `${match[2]}px`,
      };
    }

    return { width: "auto", height: "auto" };
  },

  // Cache and batch processing
  cache: new Map(),
  batch: new Map(),

  // Rendering system
  renderer: DefaultRenderer,

  // State management
  _initialized: false,
  _autoInitialized: false,
  _instances: new Map(),

  /**
   * Initialize SmartBanner system
   * @param {Object} customConfig - Custom configuration
   */
  init(customConfig = {}) {
    if (this._initialized) return;

    this.config = { ...this.config, ...customConfig };
    this._initialized = true;

    if (this.config.debug) {
      if (this.config.debug) {
        console.log("[SmartBanner] Initialized with config:", this.config);
      }
    }

    // Auto-initialize after init - only if not already auto-initialized
    if (!this._autoInitialized) {
      setTimeout(() => {
        this.autoInitialize();
      }, 100);
    }
  },

  /**
   * Create multiple banners (recommended approach)
   * @param {Array} bannerConfigs - Array of banner configurations
   * @returns {Promise<Array>} Array of rendered banner elements
   */
  async createMultiple(bannerConfigs) {
    try {
      // Validate input
      if (!Array.isArray(bannerConfigs) || bannerConfigs.length === 0) {
        throw new SmartBannerError(
          "Invalid bannerConfigs: Expected non-empty array",
          "INVALID_INPUT"
        );
      }

      // Auto-initialize if not done
      if (!this._initialized) {
        this.init();
      }

      // Extract props and validate
      const propsArray = bannerConfigs.map((config) => {
        if (!config.props || !config.container) {
          throw new SmartBannerError(
            "Each banner config must have props and container",
            "INVALID_CONFIG"
          );
        }
        return validateProps(config.props);
      });

      // Fetch banner data (single API call for all - RESTORED)
      const allBannerData = await fetchBanners(propsArray);

      // Filter and render each container with appropriate banners
      const renderedBanners = [];
      for (let i = 0; i < bannerConfigs.length; i++) {
        const config = bannerConfigs[i];
        const containerProps = config.props;

        // FIXED: Use the shared response data for all containers (single API call)
        // Each container gets the same data, filtering happens at API level
        const containerData = allBannerData[0] || []; // All containers use same response
        const matchingBanners = this._filterBannersForContainer(
          containerData,
          containerProps
        );

        if (matchingBanners && matchingBanners.length > 0) {
          const element = await this.renderBanner(
            matchingBanners,
            config.container,
            containerProps
          );
          renderedBanners.push(element);

          if (this.config.debug) {
            console.log(
              `[SmartBanner] Container ${i + 1} rendered: ${
                matchingBanners.length
              } banners`
            );
          }
        } else {
          if (this.config.debug) {
            console.log(
              `[SmartBanner] No matching banners for container ${i + 1}:`,
              containerProps
            );
          }
        }
      }

      return renderedBanners;
    } catch (error) {
      this._handleError(error);
      throw error;
    }
  },

  /**
   * Create single banner (backward compatibility)
   * @param {Object} props - Banner properties
   * @returns {Promise<HTMLElement>} Rendered banner element
   */
  async create(props) {
    // Apply hierarchical config system
    const finalProps = this.buildHierarchicalConfig(props);
    if (!props.container) {
      throw new SmartBannerError("Container is required", "MISSING_CONTAINER");
    }

    const container = props.container;
    delete props.container; // Remove container from props for API call

    return this.createMultiple([{ props: finalProps, container }]);
  },

  /**
   * Render banner to DOM
   * @param {Array} bannerData - Banner data from API
   * @param {string|HTMLElement} container - Target container
   * @param {Object} props - Banner properties
   * @returns {Promise<HTMLElement>} Rendered banner element
   */
  async renderBanner(bannerData, container, props) {
    // Debug logging - only in debug mode
    if (this.config.debug) {
      console.log("[SmartBanner] renderBanner called with:");
      console.log("- bannerData:", bannerData);
      console.log("- container:", container);
      console.log("- props:", props);
    }

    // Get container element
    const containerElement =
      typeof container === "string"
        ? document.querySelector(container)
        : container;

    if (!containerElement) {
      throw new SmartBannerError(
        `Container not found: ${container}`,
        "CONTAINER_NOT_FOUND"
      );
    }

    // Single banner rendering
    const banner = bannerData[0];

    // Debug logging - only in debug mode
    if (this.config.debug) {
      console.log("[SmartBanner] Selected banner object:", banner);
      console.log("[SmartBanner] Banner has images?:", !!banner?.images);
      console.log(
        "[SmartBanner] Banner images count:",
        banner?.images?.length || 0
      );
      if (banner?.images) {
        console.log("[SmartBanner] First image:", banner.images[0]);
      }
    }

    // Check if slider is needed - INVENTORY BASED LOGIC
    // Tek banner'ın images array'inde birden fazla görsel varsa slider
    // ANCAK inventory WAITING_AREA ise slider YAPMA (random getirme mantığı)
    const shouldUseSlider =
      banner &&
      banner.images &&
      Array.isArray(banner.images) &&
      banner.images.length > 1 &&
      banner.inventory !== "WAITING_AREA"; // WAITING_AREA ise slider yapma

    if (shouldUseSlider) {
      // Dynamic slider loading - aynı banner'da birden fazla görsel varsa slider
      const { LightweightSlider } = await import(
        "./slider/LightweightSlider.js"
      );

      // Images array'ini slider için uygun formata çevir - YENİ YAPIYA UYGUN
      const sliderItems = banner.images.map((imageItem, index) => ({
        ...banner, // Banner'ın diğer bilgilerini koru
        id: `${banner.id}-${index}`, // Unique ID
        image: this._extractSingleImageUrl(imageItem), // Görsel URL'i

        // YENİ: Her image'ın kendi click bilgileri
        url: imageItem.clickUrl || "#",
        target: imageItem.target || "_blank",
        rel: imageItem.rel || "noopener noreferrer",
        alt: imageItem.alt || imageItem.img?.alternativeText || banner.title,
        platform: imageItem.platform || [
          "DESKTOP_WEB",
          "MOBILE_WEB",
          "MOBILE_APP",
        ],

        images: [imageItem], // Tek elemanlı array - renderer için
      }));

      const slider = new LightweightSlider(
        containerElement,
        sliderItems,
        props,
        banner.inventory || "PRODUCT_HOMEPAGE" // Pass inventory for sizing
      );
      return containerElement;
    }

    // WAITING_AREA inventory için random image selection
    if (
      banner &&
      banner.inventory === "WAITING_AREA" &&
      banner.images &&
      Array.isArray(banner.images) &&
      banner.images.length > 1
    ) {
      // Random image selection for WAITING_AREA
      const randomIndex = Math.floor(Math.random() * banner.images.length);
      const selectedImage = banner.images[randomIndex];

      // Create single-image banner data for rendering - YENİ YAPIYA UYGUN
      const randomBannerData = [
        {
          ...banner,
          images: [selectedImage], // Tek random image
          image: this._extractSingleImageUrl(selectedImage), // Görsel URL'i

          // YENİ: Random seçilen image'ın click bilgileri
          url: selectedImage.clickUrl || "#",
          target: selectedImage.target || "_blank",
          rel: selectedImage.rel || "noopener noreferrer",
          alt:
            selectedImage.alt ||
            selectedImage.img?.alternativeText ||
            banner.title,
          platform: selectedImage.platform || [
            "DESKTOP_WEB",
            "MOBILE_WEB",
            "MOBILE_APP",
          ],
        },
      ];

      if (this.config.debug) {
        console.log(
          `[SmartBanner] WAITING_AREA random selection: ${randomIndex + 1}/${
            banner.images.length
          }`,
          selectedImage
        );
      }

      // Use inventory-based renderer
      DefaultRenderer.renderWithInventory(
        randomBannerData,
        containerElement,
        props,
        banner.inventory
      );

      // Track instance
      const instanceId = this._generateInstanceId();
      this._instances.set(instanceId, {
        container: containerElement,
        props,
        data: randomBannerData,
      });

      return containerElement;
    }

    // Use inventory-based renderer for regular banners
    DefaultRenderer.renderWithInventory(
      bannerData,
      containerElement,
      props,
      banner?.inventory || "PRODUCT_HOMEPAGE"
    );

    // Banner container için default style ekle
    if (containerElement.classList.contains("banner-container")) {
      containerElement.style.position = "relative";
      containerElement.style.paddingBottom = "16px";
    }

    // Track instance
    const instanceId = this._generateInstanceId();
    this._instances.set(instanceId, {
      container: containerElement,
      props,
      data: bannerData,
    });

    return containerElement;
  },

  /**
   * Generate unique instance ID
   * @returns {string} Unique ID
   */
  _generateInstanceId() {
    return `sb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Filter banners for specific container (since API returns merged results)
   * @param {Array} banners - All banners from API
   * @param {Object} containerProps - Container's specific props
   * @returns {Array} Filtered banners for this container
   */
  _filterBannersForContainer(banners, containerProps) {
    if (!banners || !Array.isArray(banners)) {
      return banners; // Return as-is if not array
    }

    // For now, return all banners since API already filtered them
    // In future, we could add more specific filtering here
    return banners;
  },

  /**
   * Extract single image URL from NEW image component structure
   * @param {Object} imageItem - Single image component (new schema)
   * @returns {string|null} Image URL with proper CDN path
   */
  _extractSingleImageUrl(imageItem) {
    if (this.config.debug) {
      console.log(
        "[SmartBanner] _extractSingleImageUrl called with:",
        imageItem
      );
    }

    if (!imageItem) return null;

    let imageUrl = null;

    // NEW: Use img.url field from transformed BFF response
    if (imageItem.img?.url) {
      imageUrl = imageItem.img.url;
      if (this.config.debug) {
        console.log("[SmartBanner] Found img.url:", imageUrl);
      }
    }
    // Fallback: direct URL field
    else if (imageItem.url) {
      imageUrl = imageItem.url;
      if (this.config.debug) {
        console.log("[SmartBanner] Found imageItem.url:", imageUrl);
      }
    } else {
      if (this.config.debug) {
        console.log("[SmartBanner] No image URL found in imageItem");
      }
    }

    // Apply CDN path for relative URLs - HER ZAMAN CDN KULLAN
    if (imageUrl && !this._isFullUrl(imageUrl)) {
      const CDN_PATH = "https://cdn2.enuygun.com/img/smart-banner/";
      const finalUrl = CDN_PATH + imageUrl.replace(/^\//, "");
      if (this.config.debug) {
        console.log("[SmartBanner] CDN image URL:", finalUrl);
      }
      return finalUrl;
    }

    if (this.config.debug) {
      console.log("[SmartBanner] Returning image URL:", imageUrl);
    }
    return imageUrl;
  },

  /**
   * Check if URL is full URL
   * @param {string} url - URL to check
   * @returns {boolean} True if full URL
   */
  _isFullUrl(url) {
    return (
      url &&
      (url.startsWith("http://") ||
        url.startsWith("https://") ||
        url.startsWith("data:"))
    );
  },

  /**
   * Handle errors globally
   * @param {Error} error - Error to handle
   */
  _handleError(error) {
    if (this.config.debug) {
      console.error("[SmartBanner] Error:", error);
    }

    // Global error handler if defined
    if (typeof this.onError === "function") {
      this.onError(error);
    }

    // Analytics tracking
    if (window.gtag) {
      window.gtag("event", "exception", {
        description: error.message,
        fatal: false,
      });
    }
  },

  /**
   * Get all active instances
   * @returns {Map} Active instances
   */
  getInstances() {
    return this._instances;
  },

  /**
   * Destroy specific instance
   * @param {string} instanceId - Instance ID to destroy
   */
  destroy(instanceId) {
    const instance = this._instances.get(instanceId);
    if (instance && instance.container) {
      instance.container.innerHTML = "";
      this._instances.delete(instanceId);
    }
  },

  /**
   * Destroy all instances
   */
  destroyAll() {
    this._instances.forEach((instance, id) => {
      this.destroy(id);
    });
    this._instances.clear();
  },

  /**
   * Hierarchical Config System
   * Merges configs from domain > page > component level
   */
  buildHierarchicalConfig(componentConfig = {}) {
    // 1. Base Config (Domain Level) - from global smart_banner_config
    const baseConfig = window.smart_banner_config || {};

    // 2. Page Config (Page Level) - from page-specific smart_banner_config
    const pageConfig = window.smart_banner_page_config || {};

    // 3. Component Config (Component Level) - from individual banner

    // Merge configurations with priority: component > page > base
    const mergedConfig = {
      ...baseConfig,
      ...pageConfig,
      ...componentConfig,
    };

    // Normalize page/pages - ensure 'pages' is always an array
    if (!mergedConfig.pages && mergedConfig.page) {
      mergedConfig.pages = Array.isArray(mergedConfig.page)
        ? mergedConfig.page
        : [mergedConfig.page];
      delete mergedConfig.page; // Remove singular version
    }

    if (this.config.debug) {
      console.log("[SmartBanner] Hierarchical Config:", {
        base: baseConfig,
        page: pageConfig,
        component: componentConfig,
        merged: mergedConfig,
      });
    }

    return mergedConfig;
  },

  /**
   * Auto-initialize banners from data attributes
   * Looks for elements with data-smart-banner attribute
   */
  autoInitialize() {
    // Prevent multiple auto-initializations
    if (this._autoInitialized) {
      if (this.config.debug) {
        console.log(
          "[SmartBanner] Auto-initialization already completed, skipping"
        );
      }
      return;
    }

    this._autoInitialized = true;

    if (this.config.debug) {
      if (this.config.debug) {
        console.log("[SmartBanner] Auto-initialization started");
      }
    }

    const bannerElements = document.querySelectorAll("[data-smart-banner]");

    if (this.config.debug) {
      console.log(
        `[SmartBanner] Found ${bannerElements.length} elements with data-smart-banner`
      );
      if (this.config.debug) {
        console.log("[SmartBanner] Elements:", bannerElements);
      }
    }

    if (bannerElements.length === 0) {
      console.warn("[SmartBanner] No data-smart-banner elements found");
      return;
    }

    const bannerConfigs = [];

    bannerElements.forEach((element, index) => {
      try {
        const dataAttr = element.getAttribute("data-smart-banner");
        let config;

        if (dataAttr) {
          // Parse JSON configuration
          config = JSON.parse(dataAttr);
        } else {
          // Fallback: read individual data attributes
          config = {
            size: element.getAttribute("data-size") || "",
            pages: element.getAttribute("data-pages")
              ? element
                  .getAttribute("data-pages")
                  .split(",")
                  .map((p) => p.trim())
              : [],
            domain: element.getAttribute("data-domain") || "",
            place: parseInt(element.getAttribute("data-place")) || 1,
            env: element.getAttribute("data-env")?.split(",") || [],
            sliderCount: element.getAttribute("data-slider-count")
              ? parseInt(element.getAttribute("data-slider-count"))
              : undefined,
          };
        }

        // Apply hierarchical config system
        const finalConfig = this.buildHierarchicalConfig(config);

        bannerConfigs.push({
          container: element,
          props: finalConfig,
        });

        if (this.config.debug) {
          console.log(
            `[SmartBanner] Auto-init banner ${index + 1}:`,
            finalConfig
          );
        }
      } catch (error) {
        console.warn(
          `[SmartBanner] Failed to parse banner config for element ${index}:`,
          error
        );
        // Skip invalid configurations but continue with others
      }
    });

    // Create all banners at once
    if (bannerConfigs.length > 0) {
      if (this.config.debug) {
        console.log(
          `[SmartBanner] Auto-init: ${bannerConfigs.length} banner config found`,
          bannerConfigs
        );
      }

      this.createMultiple(bannerConfigs)
        .then((results) => {
          if (this.config.debug) {
            console.log(
              `[SmartBanner] Auto-init success: ${results.length} banners rendered`,
              results
            );
          }
        })
        .catch((error) => {
          console.error("[SmartBanner] Auto-initialization failed:", error);
          if (this.config.debug) {
            console.error(
              "[SmartBanner] Banner configs that failed:",
              bannerConfigs
            );
          }
        });
    } else {
      if (this.config.debug) {
        if (this.config.debug) {
          console.log("[SmartBanner] Auto-init: No banner configs found");
        }
      }
    }
  },
};

// Auto-initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    if (!window.SmartBanner._initialized) {
      window.SmartBanner.init();
    }
  });
} else {
  // DOM is already ready
  if (!window.SmartBanner._initialized) {
    window.SmartBanner.init();
  }
}

// Export DefaultRenderer and sizeUtils to window for debugging
window.SmartBanner.DefaultRenderer = DefaultRenderer;
window.SmartBanner.sizeUtils = sizeUtils;

// Export for module systems
export default window.SmartBanner;
